import { lazy, Suspense, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { HOMEWORK_MATERIAL_MAX, HOMEWORK_TASK_TEXT_MAX } from "@edu/shared";
import { overflowText, roomLeft, taskTextCounter, thumbOfMediaFile } from "./material.ts";
import { MaterialStrip } from "./MaterialStrip.tsx";
import type { MaterialState } from "./use-material.ts";

/**
 * ⚠️ Только через `import()`: форма выдачи грузится статически (`App.tsx`), а
 * диалог выбора тянет дерево папок и список материалов. Статический импорт увёз
 * бы его в стартовый чанк всем, включая ученика (5.1 CLAUDE.md).
 */
const MediaPicker = lazy(() =>
  import("../../media/MediaPicker.tsx").then((m) => ({ default: m.MediaPicker })),
);

/**
 * Блок «Материал» формы выдачи: текст задания и картинки (1.1-1.2
 * `docs/homework-scratch-design.md`).
 *
 * Стоит ПЕРЕД блоком «Задания»: домашка бывает из одного материала, без единого
 * задания, и тогда это единственное, что форма спрашивает после «Кому».
 *
 * ⚠️ Текст задания это БОЛЬШОЕ поле (просьба владельца 26.09.2026): туда
 * вставляют условие задачи, абзац для чтения, список номеров. Поле растёт по
 * содержимому, переносы уезжают как есть.
 */
export function MaterialBlock(props: {
  material: MaterialState;
  busy: boolean;
  /** Лента «С доски урока»: есть только при форме из карточки занятия и одном ученике. */
  lessonImages: ReactNode;
}) {
  const { material, busy } = props;
  const counter = taskTextCounter(material.taskText.length);
  const room = roomLeft(material.items);
  const overflow = overflowText(material.overflow);

  return (
    <section className="card hwmat">
      <h2>Материал</h2>

      <label className="hwmat__field">
        <span>Текст задания</span>
        <GrowingArea
          value={material.taskText}
          disabled={busy}
          onChange={material.setTaskText}
        />
      </label>
      {counter !== null && <p className="hint">{counter}</p>}
      {/*
        Постоянная строка, а не появляющаяся при вставке адреса: ссылку
        вставляют и до того, как узнают, что она станет нажимаемой (Р10
        docs/homework-media-plan.md). Отметку нажатия видно в карточке домашки.
      */}
      <p className="hint">
        Ссылку, которая начинается с http:// или https://, ученик откроет нажатием. В
        карточке домашки будет видно, нажимали ли на неё.
      </p>

      <MaterialStrip material={material} busy={busy} />

      <p className="acts">
        <DevicePicker disabled={busy || room === 0} onFiles={material.addDeviceFiles} />
        <LibraryPicker disabled={busy || room === 0} material={material} />
      </p>
      {overflow !== null ? (
        <p className="muted">{overflow}</p>
      ) : (
        room === 0 && (
          <p className="muted">
            Больше {HOMEWORK_MATERIAL_MAX} файлов к одной домашке не добавить.
          </p>
        )
      )}

      {props.lessonImages}
    </section>
  );
}

/**
 * Многострочное поле, растущее по содержимому. Высота ставится в `style`,
 * потому что она мера текста, а не оформления: CSS её знать не может.
 */
function GrowingArea(props: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [props.value]);
  return (
    <textarea
      ref={ref}
      className="fld hwmat__text"
      rows={4}
      maxLength={HOMEWORK_TASK_TEXT_MAX}
      value={props.value}
      disabled={props.disabled}
      placeholder="Условие задачи, текст для чтения или номера из учебника"
      onChange={(e) => props.onChange(e.target.value)}
    />
  );
}

/**
 * Кнопка вместо голого `<input type="file">`: у того подпись задаёт браузер.
 * Без `capture`: снятое вчера лежит в галерее, а `capture` открыл бы камеру.
 */
function DevicePicker(props: { disabled: boolean; onFiles: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className="btn"
        disabled={props.disabled}
        onClick={() => input.current?.click()}
      >
        Добавить картинку
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          // Сброс сразу: без него повторный выбор того же файла не даёт события
          e.target.value = "";
          if (files.length > 0) props.onFiles(files);
        }}
      />
    </>
  );
}

/**
 * «Из Материалов»: картинки, аудио и видео из медиатеки преподавателя (Р3 и В3
 * `docs/homework-media-plan.md`). Загрузки из формы нет: ссылка в подвале
 * открывает Материалы в новой вкладке, чтобы заполненная форма не пропала, а
 * повторное открытие диалога перечитывает папку и показывает новый файл.
 *
 * Файлы ученика спрятаны (`ownOnly`): сервер их в материал не примет.
 */
function LibraryPicker(props: { disabled: boolean; material: MaterialState }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn" disabled={props.disabled} onClick={() => setOpen(true)}>
        Из Материалов
      </button>
      {open && (
        <Suspense fallback={null}>
          <MediaPicker
            title="Добавить из Материалов"
            kinds={["image", "audio", "video"]}
            ownOnly
            pickLabel="Добавить"
            foot={
              <>
                <a href="/media" target="_blank" rel="noopener">
                  Загрузить файл в Материалы
                </a>{" "}
                · откроется в новой вкладке
              </>
            }
            onPick={(file) => {
              const thumb = thumbOfMediaFile(file);
              if (thumb !== null) props.material.addMedia(file.id, thumb);
              setOpen(false);
            }}
            onClose={() => setOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
}
